import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import { Brain, Zap, Shield, Cpu, Activity, Globe, Wallet, Box, Users, Coins, Percent, Fingerprint, Database, Network, ChevronRight, Maximize2, ZoomIn, ZoomOut, Info } from 'lucide-react';

interface Node {
    id: string;
    label: string;
    labelAr: string;
    type: 'core' | 'logic' | 'asset' | 'financial' | 'bridge';
    x: number;
    y: number;
    description: string;
    longDescriptionAr: string;
    icon: any;
    color: string;
}

interface Edge {
    from: string;
    to: string;
    type: 'data' | 'funds' | 'signal';
}

const NODES: Node[] = [
    // THE CORE: CENTRAL INTELLIGENCE
    {
        id: 'cyber_core',
        label: 'Cyber-Core Central',
        labelAr: 'النواة الذكية المركزية',
        type: 'core', x: 0, y: 0,
        description: 'The master state engine (useStore) syncing UI, Balance, and Auth.',
        longDescriptionAr: 'المحرك الأساسي للنظام (Zustand) الذي يدير حالة المستخدم، الأرصدة، والمصادقة لحظياً.',
        icon: Cpu, color: '#3390EC'
    },

    // LAYER 1: INTERNAL LOGIC & BRAIN
    {
        id: 'p2p_logic',
        label: 'P2P Synapse',
        labelAr: 'عصب التبادل المباشر',
        type: 'logic', x: -180, y: -120,
        description: 'Secure transaction logic for peer-to-peer asset validation.',
        longDescriptionAr: 'منطق التداول الآمن الذي يضمن صحة المعاملات بين الأطراف بشكل مباشر.',
        icon: Zap, color: '#F59E0B'
    },
    {
        id: 'auction_brain',
        label: 'Auction Cerebrum',
        labelAr: 'قشرة المزادات الذكية',
        type: 'logic', x: 180, y: -120,
        description: 'Bidding calculation engine with millisecond precision.',
        longDescriptionAr: 'محرك حساب المزايدات الذي يعمل بدقة أجزاء من الثانية لضمان العدالة.',
        icon: Activity, color: '#F43F5E'
    },

    // LAYER 2: ASSET & IDENTITY
    {
        id: 'identity',
        label: 'User Bio-ID',
        labelAr: 'الهوية الحيوية',
        type: 'asset', x: 0, y: -250,
        description: 'User profiles, rank systems, and XP advancement threads.',
        longDescriptionAr: 'إدارة السجلات، الرتب، وتطور الخبرة (XP) لجميع المستخدمين.',
        icon: Fingerprint, color: '#A855F7'
    },
    {
        id: 'nft_vault',
        label: 'Digital Asset Vault',
        labelAr: 'خزنة الأصول الرقمية',
        type: 'asset', x: 320, y: 50,
        description: 'Unified storage for Cards, Skins, and Username NFTs.',
        longDescriptionAr: 'المخزن الموحد للبطاقات، الأغلفة، والأسماء النادرة كأصول رقمية.',
        icon: Box, color: '#10B981'
    },

    // LAYER 3: FINANCIAL BLOODSTREAM
    {
        id: 'wallet_stream',
        label: 'Wallet Bloodstream',
        labelAr: 'شريان المحافظ المالية',
        type: 'financial', x: -320, y: 50,
        description: 'Global GRAM liquidity flow and balance synchronization.',
        longDescriptionAr: 'تدفق السيولة بعملة GRAM ومزامنة أرصدة المحافظ العالمية.',
        icon: Wallet, color: '#3B82F6'
    },
    {
        id: 'revenue_node',
        label: 'Revenue Engine',
        labelAr: 'محرك الإيرادات والعمولات',
        type: 'financial', x: -250, y: 220,
        description: 'System-wide fee harvesting (1.5% - 5%) per movement.',
        longDescriptionAr: 'نظام جني الرسوم التلقائي من كل حركة بيع أو تبادل داخل المنظومة.',
        icon: Percent, color: '#10B981'
    },

    // LAYER 4: EXTERNAL BRIDGES
    {
        id: 'blockchain',
        label: 'Blockchain Synapse',
        labelAr: 'جسر البلوكشين (GRAM)',
        type: 'bridge', x: 250, y: 220,
        description: 'The secure link to the GRAM Decentralized Network.',
        longDescriptionAr: 'الرابط المؤمن مع شبكة GRAM اللامركزية لتوثيق الملكية الحقيقية.',
        icon: Globe, color: '#0EA5E9'
    }
];

const EDGES: Edge[] = [
    { from: 'cyber_core', to: 'p2p_logic', type: 'data' },
    { from: 'cyber_core', to: 'auction_brain', type: 'data' },
    { from: 'cyber_core', to: 'identity', type: 'data' },
    { from: 'cyber_core', to: 'wallet_stream', type: 'funds' },
    { from: 'wallet_stream', to: 'p2p_logic', type: 'funds' },
    { from: 'p2p_logic', to: 'revenue_node', type: 'funds' },
    { from: 'auction_brain', to: 'revenue_node', type: 'funds' },
    { from: 'nft_vault', to: 'auction_brain', type: 'data' },
    { from: 'nft_vault', to: 'blockchain', type: 'signal' },
    { from: 'wallet_stream', to: 'blockchain', type: 'signal' }
];

export const SystemMap = () => {
    const [zoom, setZoom] = useState(0.85);
    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [isHovered, setIsHovered] = useState<string | null>(null);
    const canvasX = useMotionValue(0);
    const canvasY = useMotionValue(0);
    const canvasScale = useMotionValue(0.85);

    useEffect(() => {
        animate(canvasScale, zoom, { type: 'spring', stiffness: 200, damping: 25 });
    }, [zoom]);

    const handleZoom = (delta: number) => setZoom(z => Math.min(Math.max(z + delta, 0.4), 1.5));

    return (
        <div className="relative w-full h-[750px] bg-[#050507] rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl group/map select-none font-sans">
            {/* AMBIENT BACKGROUND GLOWS */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-40">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 blur-[120px] rounded-full" />
                <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-purple-500/5 blur-[100px] rounded-full" />
            </div>

            {/* CYBER GRID */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
                backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
                backgroundSize: '60px 60px'
            }} />

            {/* HEADER DESIGN */}
            <div className="absolute top-10 left-10 z-50 flex items-start gap-4">
                <div className="relative">
                    <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full animate-pulse" />
                    <div className="relative p-4 bg-black/40 border border-white/10 rounded-2xl backdrop-blur-3xl">
                        <Network size={32} className="text-[#3390EC]" />
                    </div>
                </div>
                <div className="pt-1">
                    <h1 className="text-3xl font-black text-white tracking-tighter uppercase leading-none mb-1">Architecture <span className="text-blue-500">Intelligence</span></h1>
                    <div className="flex items-center gap-3">
                        <div className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded-md">
                            <span className="text-[10px] font-black text-blue-400 tracking-widest uppercase">System Blueprint 5.0</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                            <span className="text-[10px] font-black text-emerald-400/60 tracking-widest uppercase">Neural Links Stable</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* CANVAS INTERACTION */}
            <motion.div
                className="w-full h-full cursor-grab active:cursor-grabbing flex items-center justify-center p-20"
                drag dragMomentum={false}
                style={{ x: canvasX, y: canvasY }}
            >
                <motion.div style={{ scale: canvasScale }} className="relative">
                    {/* SVG NEURAL EDGES */}
                    <svg className="absolute inset-0 pointer-events-none overflow-visible" width="1" height="1">
                        <defs>
                            <filter id="glow">
                                <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                                <feMerge>
                                    <feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </defs>
                        {EDGES.map((edge, i) => {
                            const n1 = NODES.find(n => n.id === edge.from)!;
                            const n2 = NODES.find(n => n.id === edge.to)!;
                            const isEdgeHighlighted = isHovered === edge.from || isHovered === edge.to;
                            return (
                                <g key={i}>
                                    <motion.path
                                        d={`M ${n1.x} ${n1.y} C ${(n1.x + n2.x) / 2} ${n1.y}, ${(n2.x + n1.x) / 2} ${n2.y}, ${n2.x} ${n2.y}`}
                                        fill="none"
                                        stroke={isEdgeHighlighted ? '#3390EC' : 'rgba(255,255,255,0.05)'}
                                        strokeWidth={isEdgeHighlighted ? 3 : 1.5}
                                        filter={isEdgeHighlighted ? "url(#glow)" : ""}
                                        transition={{ duration: 0.3 }}
                                    />
                                    {isEdgeHighlighted && (
                                        <motion.circle r="3" fill="#3390EC">
                                            <animateMotion dur="2s" repeatCount="indefinite" path={`M ${n1.x} ${n1.y} C ${(n1.x + n2.x) / 2} ${n1.y}, ${(n2.x + n1.x) / 2} ${n2.y}, ${n2.x} ${n2.y}`} />
                                        </motion.circle>
                                    )}
                                </g>
                            );
                        })}
                    </svg>

                    {/* NEURAL NODES */}
                    {NODES.map((node) => (
                        <motion.div
                            key={node.id}
                            className={`absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${selectedNode && selectedNode.id !== node.id ? 'opacity-20 scale-90 blur-sm' : ''}`}
                            style={{ left: node.x, top: node.y }}
                            onMouseEnter={() => setIsHovered(node.id)}
                            onMouseLeave={() => setIsHovered(null)}
                            onClick={() => setSelectedNode(node)}
                        >
                            <div className="relative flex flex-col items-center">
                                {/* NODE CORE */}
                                <div className={`relative w-24 h-24 rounded-3xl flex items-center justify-center border-2 transition-all duration-300 cursor-pointer shadow-2xl ${isHovered === node.id ? 'scale-110' : ''}`}
                                    style={{
                                        borderColor: isHovered === node.id ? node.color : 'rgba(255,255,255,0.07)',
                                        backgroundColor: isHovered === node.id ? `${node.color}22` : 'rgba(0,0,0,0.6)',
                                        boxShadow: isHovered === node.id ? `0 0 40px ${node.color}33` : 'none'
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-[calc(1.5rem-2px)]" />
                                    <node.icon size={40} style={{ color: node.color }} className={isHovered === node.id ? 'animate-pulse' : ''} />

                                    {/* STATUS ORBIT */}
                                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-[#050507]" style={{ backgroundColor: node.color }} />
                                </div>

                                {/* LABELS */}
                                <div className="mt-4 flex flex-col items-center gap-0.5">
                                    <span className="text-[13px] font-black text-white uppercase tracking-wider">{node.labelAr}</span>
                                    <span className="text-[9px] font-bold text-white/30 uppercase tracking-[0.2em]">{node.label}</span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.div>

            {/* INTERACTIVE HUD DRAWER */}
            <AnimatePresence>
                {selectedNode && (
                    <motion.div
                        initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }}
                        className="absolute top-0 right-0 w-[400px] h-full bg-black/60 backdrop-blur-[60px] border-l border-white/5 p-10 flex flex-col shadow-[-40px_0_100px_rgba(0,0,0,0.5)] z-[100]"
                    >
                        <button onClick={() => setSelectedNode(null)} className="absolute top-10 right-10 p-3 hover:bg-white/5 rounded-2xl transition-all">
                            <Maximize2 size={24} className="text-white/20 rotate-45" />
                        </button>

                        <div className="mt-12 flex flex-col items-center text-center">
                            <div className="w-32 h-32 rounded-[2.5rem] flex items-center justify-center mb-8 relative" style={{ backgroundColor: `${selectedNode.color}15` }}>
                                <div className="absolute inset-0 border-2 border-dashed rounded-[2.5rem] animate-[spin_10s_linear_infinite] opacity-20" style={{ borderColor: selectedNode.color }} />
                                <selectedNode.icon size={64} style={{ color: selectedNode.color }} />
                            </div>

                            <h2 className="text-3xl font-black text-white mb-1 tracking-tighter">{selectedNode.labelAr}</h2>
                            <p className="text-[10px] font-black uppercase tracking-[0.3em] mb-8" style={{ color: selectedNode.color }}>{selectedNode.label}</p>

                            <div className="w-full p-6 bg-white/5 rounded-3xl border border-white/10 mb-8">
                                <p className="text-lg text-white/70 font-medium leading-relaxed">{selectedNode.longDescriptionAr}</p>
                            </div>

                            <div className="w-full text-left space-y-4">
                                <h4 className="text-[10px] font-black text-white/30 uppercase tracking-widest pl-2">Neural Connected Paths</h4>
                                <div className="space-y-2">
                                    {EDGES.filter(e => e.from === selectedNode.id || e.to === selectedNode.id).map((edge, i) => {
                                        const otherId = edge.from === selectedNode.id ? edge.to : edge.from;
                                        const other = NODES.find(n => n.id === otherId)!;
                                        return (
                                            <div key={i} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5 hover:bg-white/10 transition-all group">
                                                <div className="flex items-center gap-3">
                                                    <other.icon size={18} style={{ color: other.color }} />
                                                    <span className="text-[11px] font-bold text-white/60">{other.labelAr}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[8px] font-black text-white/20 uppercase">{edge.type}</span>
                                                    <ChevronRight size={14} className="text-white/20 group-hover:translate-x-1 transition-transform" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="mt-auto">
                            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center gap-4">
                                <Shield className="text-blue-400" size={24} />
                                <p className="text-[10px] text-blue-300/80 font-bold leading-tight uppercase tracking-wider">
                                    Secure node verification active. All synaptic data is encrypted.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* CONTROLS */}
            <div className="absolute bottom-10 left-10 z-50 flex items-center gap-3">
                <div className="flex bg-black/40 backdrop-blur-3xl border border-white/10 rounded-2xl p-1.5">
                    <button onClick={() => handleZoom(0.1)} className="p-3 hover:bg-white/5 rounded-xl text-white/40 hover:text-white transition-all active:scale-90"><ZoomIn size={20} /></button>
                    <div className="w-px bg-white/10 mx-1 my-2" />
                    <button onClick={() => handleZoom(-0.1)} className="p-3 hover:bg-white/5 rounded-xl text-white/40 hover:text-white transition-all active:scale-90"><ZoomOut size={20} /></button>
                </div>
                {!selectedNode && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl">
                        <Info size={14} className="text-blue-400" />
                        <span className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">تفاعل مع العقد لاستكشاف عمق النظام</span>
                    </motion.div>
                )}
            </div>
        </div>
    );
};
